/**
 * 🎯 P1.2 — PAGE NOTIFICATIONS (Actions suggérées Owen)
 * 
 * RÈGLE MOA STRICTE:
 * - Affiche UNIQUEMENT les actions suggérées (pas le raisonnement interne)
 * - Statuts : pending / done / dismissed
 * - ZÉRO logique pédagogique côté UI
 * - Pinecone JAMAIS interrogé par le frontend
 * - POINT D'ENTRÉE UNIQUE: Badge dans TrainingDashboard
 * 
 * Cette page interroge uniquement l'API MongoDB via:
 * - GET /api/owen/feedbacks
 * - PUT /api/owen/feedbacks/{id}/status
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, 
  X, 
  ArrowLeft,
  Bell,
  Sparkles,
  ChevronRight,
  Filter,
  PartyPopper
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 🎉 Animation de célébration
const triggerCelebration = () => {
  // Confetti burst
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#FFD700', '#10B981', '#8B5CF6', '#F59E0B']
  });
};

// Composant pour une carte d'action
const ActionCard = ({ feedback, onUpdateStatus, isUpdating, onCelebrate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Déterminer le préfixe selon la source
  const sourcePrefix = feedback.source === 'certifiant' ? '🎓' : '🧠';
  const sourceLabel = feedback.source === 'certifiant' ? 'Parcours certifiant' : 'Entraînement libre';
  
  // Titre amélioré avec nom du module pour certifiant
  const getDisplayTitle = () => {
    if (feedback.source === 'certifiant') {
      // Si theme_label contient le titre du module (enrichi par le backend)
      const moduleTitle = feedback.theme_label || `Module ${feedback.module_id || ''}`;
      return `${moduleTitle} — Action de progrès`;
    }
    // Pour entraînement libre
    return feedback.theme_label || 'Action suggérée';
  };
  
  const displayTitle = getDisplayTitle();
  
  const statusConfig = {
    pending: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      badge: 'bg-amber-100 text-amber-700',
      label: 'À faire'
    },
    done: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      badge: 'bg-emerald-100 text-emerald-700',
      label: 'Fait'
    },
    dismissed: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      badge: 'bg-gray-100 text-gray-500',
      label: 'Ignoré'
    }
  };
  
  const config = statusConfig[feedback.statut] || statusConfig.pending;
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`${config.bg} ${config.border} border rounded-2xl p-5 transition-all duration-300`}
    >
      {/* Header avec préfixe source */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Badge source avec emoji */}
          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
            feedback.source === 'certifiant' 
              ? 'bg-indigo-100 text-indigo-700' 
              : 'bg-teal-100 text-teal-700'
          }`}>
            {sourcePrefix} {sourceLabel}
          </span>
          {/* Badge statut */}
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.badge}`}>
            {config.label}
          </span>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
          {formatDate(feedback.created_at || feedback.timestamp)}
        </span>
      </div>
      
      {/* Contenu de l'action */}
      <div 
        className="cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Titre + icône */}
        <div className="flex items-start gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <h3 className="text-base font-semibold text-gray-900">
            {displayTitle}
          </h3>
        </div>
        
        {/* 🎯 P1.2: Affichage structuré pour entraînement libre */}
        {feedback.source === 'libre' && feedback.axe_evolution ? (
          <div className="space-y-3 pl-7">
            {/* Axe d'évolution */}
            <div className="bg-amber-50/70 rounded-lg p-3 border border-amber-100">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-sm">🎯</span>
                <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Axe d&apos;évolution</span>
              </div>
              <p className="text-amber-900 text-sm leading-relaxed">{feedback.axe_evolution}</p>
            </div>
            
            {/* Relance (action suggérée) */}
            <div className="bg-violet-50/70 rounded-lg p-3 border border-violet-100">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-sm">❓</span>
                <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Relance</span>
              </div>
              <p className="text-violet-900 text-sm leading-relaxed font-medium">{feedback.action_suggeree}</p>
            </div>
          </div>
        ) : (
          /* Affichage standard pour certifiant */
          <p className="text-gray-700 leading-relaxed pl-7">
            {feedback.action_suggeree}
          </p>
        )}
        
        {/* Détails expandables (observation) */}
        <AnimatePresence>
          {isExpanded && feedback.observation && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-gray-200 pl-7"
            >
              <div className="bg-blue-50/70 rounded-lg p-3 border border-blue-100">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-sm">🔍</span>
                  <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Observation</span>
                </div>
                <p className="text-blue-900 text-sm leading-relaxed">{feedback.observation}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {feedback.observation && (
          <button className="mt-2 ml-7 text-sm text-violet-600 flex items-center gap-1 hover:text-violet-800">
            {isExpanded ? 'Moins de détails' : 'Plus de détails'}
            <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>
        )}
      </div>
      
      {/* Actions */}
      {feedback.statut === 'pending' && (
        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={() => {
              onCelebrate();
              onUpdateStatus(feedback.id, 'done');
            }}
            disabled={isUpdating}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50"
            data-testid={`action-done-${feedback.id}`}
          >
            <CheckCircle2 className="w-5 h-5" />
            C&apos;est fait !
          </button>
          <button
            onClick={() => onUpdateStatus(feedback.id, 'dismissed')}
            disabled={isUpdating}
            className="px-4 py-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
            data-testid={`action-dismiss-${feedback.id}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      
      {/* Badge pour done/dismissed */}
      {feedback.statut === 'done' && (
        <div className="mt-4 pt-4 border-t border-emerald-200 flex items-center gap-2 text-emerald-600">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Bravo ! Action accomplie</span>
        </div>
      )}
    </motion.div>
  );
};

// Composant État vide
const EmptyState = ({ filter }) => {
  const messages = {
    all: "Aucune action suggérée pour le moment. Continue tes échanges avec Owen !",
    pending: "Aucune action en attente. Tu as tout accompli !",
    done: "Aucune action marquée comme faite.",
    dismissed: "Aucune action ignorée."
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="text-center py-16"
    >
      <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
        <Bell className="w-10 h-10 text-amber-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">
        {filter === 'pending' ? 'Tout est fait !' : 'Rien à afficher'}
      </h3>
      <p className="text-gray-500 max-w-md mx-auto">
        {messages[filter] || messages.all}
      </p>
    </motion.div>
  );
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [feedbacks, setFeedbacks] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, done: 0, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending'); // pending | done | all
  
  // Charger les feedbacks
  const loadFeedbacks = useCallback(async () => {
    if (!token) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Charger feedbacks et counts en parallèle
      const [feedbacksRes, countsRes] = await Promise.all([
        fetch(`${API_URL}/api/owen/feedbacks${filter !== 'all' ? `?statut=${filter}` : ''}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/owen/feedbacks/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (!feedbacksRes.ok || !countsRes.ok) {
        throw new Error('Erreur lors du chargement');
      }
      
      const [feedbacksData, countsData] = await Promise.all([
        feedbacksRes.json(),
        countsRes.json()
      ]);
      
      setFeedbacks(feedbacksData);
      setCounts(countsData);
    } catch (err) {
      console.error('Erreur chargement feedbacks:', err);
      setError('Impossible de charger les notifications');
    } finally {
      setIsLoading(false);
    }
  }, [token, filter]);
  
  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);
  
  // Mettre à jour le statut avec MUTATION IMMÉDIATE
  const handleUpdateStatus = async (feedbackId, newStatus) => {
    if (!token) return;
    
    try {
      setIsUpdating(true);
      
      // 🎯 P1.2 ACTION 3: MUTATION UI IMMÉDIATE (optimistic update)
      // Retirer immédiatement de la liste locale AVANT l'appel API
      const previousFeedbacks = [...feedbacks];
      const previousCounts = { ...counts };
      
      // 1. Retirer l'action de la liste actuelle (si filtre 'pending')
      if (filter === 'pending') {
        setFeedbacks(prev => prev.filter(f => f.id !== feedbackId));
      } else {
        // Mettre à jour le statut dans la liste
        setFeedbacks(prev => prev.map(f => 
          f.id === feedbackId ? { ...f, statut: newStatus } : f
        ));
      }
      
      // 2. Mettre à jour les compteurs immédiatement
      if (newStatus === 'done') {
        setCounts(prev => ({
          ...prev,
          pending: Math.max(0, prev.pending - 1),
          done: prev.done + 1
        }));
      } else if (newStatus === 'dismissed') {
        setCounts(prev => ({
          ...prev,
          pending: Math.max(0, prev.pending - 1)
        }));
      }
      
      console.log('[P1.2] 🎯 frontend_state_updated=true - Mutation UI immédiate appliquée');
      
      // 3. Appel API (en arrière-plan)
      const res = await fetch(`${API_URL}/api/owen/feedbacks/${feedbackId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ statut: newStatus })
      });
      
      if (!res.ok) {
        // Rollback en cas d'erreur
        setFeedbacks(previousFeedbacks);
        setCounts(previousCounts);
        throw new Error('Erreur lors de la mise à jour');
      }
      
      console.log('[P1.2] ✅ status=done - Backend update success');
      
      // 4. Recharger pour synchroniser (non-bloquant)
      // Note: le state local est déjà correct, ce reload est juste pour sync
      loadFeedbacks();
      
    } catch (err) {
      console.error('Erreur mise à jour statut:', err);
      setError('Impossible de mettre à jour le statut');
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Filtres
  const filters = [
    { key: 'pending', label: 'À faire', count: counts.pending },
    { key: 'done', label: 'Faits', count: counts.done },
    { key: 'all', label: 'Tous', count: counts.total }
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-amber-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Retour</span>
            </button>
            
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="w-6 h-6 text-amber-500" />
              Mes Actions
            </h1>
            
            <div className="w-20" /> {/* Spacer */}
          </div>
        </div>
      </div>
      
      {/* Contenu */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Filtres */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2" data-testid="filters">
          <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                filter === f.key
                  ? 'bg-amber-500 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-amber-50 border border-gray-200'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
              {f.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  filter === f.key ? 'bg-amber-600' : 'bg-gray-100'
                }`}>
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Erreur */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}
        
        {/* Chargement */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : feedbacks.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          /* Liste des feedbacks */
          <div className="space-y-4" data-testid="feedbacks-list">
            <AnimatePresence mode="popLayout">
              {feedbacks.map((feedback) => (
                <ActionCard
                  key={feedback.id}
                  feedback={feedback}
                  onUpdateStatus={handleUpdateStatus}
                  isUpdating={isUpdating}
                  onCelebrate={triggerCelebration}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
        
        {/* Message encourageant si pending = 0 */}
        {!isLoading && filter === 'pending' && feedbacks.length === 0 && counts.done > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 text-center"
          >
            <h3 className="text-lg font-semibold text-emerald-700 mb-2">
              Bravo ! Tu as accompli {counts.done} action{counts.done > 1 ? 's' : ''} 🎉
            </h3>
            <p className="text-emerald-600">
              Continue tes échanges avec Owen pour recevoir de nouvelles suggestions.
            </p>
          </motion.div>
        )}
        
        {/* Safe area padding pour mobile */}
        <div className="h-24 md:h-16" aria-hidden="true" />
      </div>
    </div>
  );
}
